/**
 * @GestionalePD Admin national federation details UI
 */
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  Tabs,
  Tab,
  Container,
  Box,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Card,
  Grid,
  TextField,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TableFooter,
  TablePagination,
  styled,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Tooltip,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowRight,
  Assignment as AssignmentIcon,
  GppGood as GppGoodIcon,
  AccountTreeOutlined as AccountTreeIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LaunchIcon from '@mui/icons-material/Launch';
import EditIcon from '@mui/icons-material/Edit';
//-----------------------------------------------------------------------------------------------
import { CustomAutocomplete, CustomTextField, CustomDatePicker } from '../../utils/StyledInputs';
import {
  PopAnimation,
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_DELAY,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import {
  DefaultContainedButton,
  DefaultOutlinedButton,
  RedOutlinedButton,
  CustomIconButton,
} from '../../utils/Buttons';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { HttpErrorHandler, ErrorUI } from '../../utils/ErrorHandler';
import { a11yProps, ThripleTabPanel } from '../../utils/TabStyles';
import { EmptyPageUI, LoadingUI } from '../../utils/LayoutStyles';
import { TablePaginationActions } from '../../utils/TableStyles';
import { validatePhoneNumber } from '../../utils/numberValidity';
import { EntitiesDialog } from '../../utils/EntitiesDialog';
import { DecisionDialog } from '../../utils/DecisionDialog';
import { UserFilterDialog } from '../../utils/UserFilterDialog';
import { PositionStatusLabel } from '../../utils/PositionStatusLabel';
import { entityTypeLabel } from '../../utils/EntityTypeLabel';
import API from '../../api';

// custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

/**
 * @returns Admin national federation details UI
 */
export default function NationalFederationDetail() {
  // general
  const navigate = useNavigate();
  const { state } = useLocation();
  const { federationID } = state;

  const [isModify, setModify] = useState(false);
  const [positions, setPositions] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [hasFederation, setHasFederation] = useState(false);
  const [hasCreationSuccess, setHasCreationSuccess] = useState(false);
  const [currentProfile, setCurrentProfile] = useState('');
  const [canModifyEntity, setCanModifyEntity] = useState(false);
  const [italy, setItaly] = useState({});

  // Federation data
  const [federationEntityType, setFederationEntityType] = useState('');
  const [federationName, setFederationName] = useState('');
  const [federationEmail, setFederationEmail] = useState('');
  const [federationCallCode, setFederationCallCode] = useState('');
  const [federationPhone, setFederationPhone] = useState('');
  const [federationStreet, setFederationStreet] = useState('');
  const [federationPostalCode, setFederationPostalCode] = useState('');
  const [federationCity, setFederationCity] = useState({});
  const [federationCityName, setFederationCityName] = useState('');
  const [federationProvince, setFederationProvince] = useState({});
  const [federationRegion, setFederationRegion] = useState({});
  const [federationRegionName, setFederationRegionName] = useState('');
  const [federationCountry, setFederationCountry] = useState({});
  const [federationEntityData, setFederationEntityData] = useState([]);
  const [federationIsMaster, setFederationIsMaster] = useState(false);
  const [federationParents, setFederationParents] = useState([]);

  // address api data
  const [cityList, setCityList] = useState([]);
  const [provinceList, setProvinceList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [chargePositionList, setChargePositionList] = useState([]);
  const [entityTypeList, setEntityTypeList] = useState([]);

  // table pagination handler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - positions.length) : 0;

  // change page handler
  const handleChangePage = (event, newPage) => setPage(newPage);

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState('noDialog');

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}  >
          {hasCreationSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  // snackbar handler
  const {
    open: openEditFederationDataSnack,
    handleOpen: handleOpenEditFederationDataSnack,
    handleClose: handleCloseEditFederationDataSnack,
  } = CreateSnackbarFunctions(false);

  const {
    open: openFederationNewPositionDataSnack,
    handleOpen: handleOpenFederationNewPositionDataSnack,
    handleClose: handleCloseFederationNewPositionDataSnack,
  } = CreateSnackbarFunctions(false);

  // tabs handler
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => setValue(newValue);

  // Add entity parent request
  const onAddParentEntity = (id) => {
    const relData = {
      parent_id: id,
      entity_id: federationID,
    };

    API.post(`/erels`, relData)
      .then((response) => {
        setHasCreationSuccess(true);
        refreshCharges();
        setPopUpText('Informazioni salvate');

        setOpen(true);
        setTimeout(() => {
          setOpen(false);
        }, POPUP_DURATION_SUCCESS);
      })
      .catch((error) => {
        setHasCreationSuccess(false);
        HttpErrorHandler(error, setPopUpText);
        setOpen(true);

        setTimeout(() => {
          setOpen(false);
        }, POPUP_DURATION_ERROR);
      });
  };

  // new charge dialog handler
  const [openAddChargeDialog, setOpenAddChargeDialog] = useState(false);
  const [chargePosition, setChargePosition] = useState([]);
  const [chargeInstitutionalEmail, setChargeInstitutionalEmail] = useState('');
  const [chargeStartDate, setChargeStartDate] = useState('');
  const [chargeEndDate, setChargeEndDate] = useState('');

  // change start date handler
  const handleStartDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setChargeStartDate(formattedDate);
  };

  // change end date handler
  const handleEndDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setChargeEndDate(formattedDate);
  };

  // open new charge dialog handler
  const onOpenAddChargeDialog = () => {
    setIsDialogOpen('newPosition');
    setOpenAddChargeDialog(true);
    getChargeApi();
  };

  // close new charge dialog handler
  const onCloseAddChargeDialog = () => {
    setIsDialogOpen('noDialog');
    setOpenAddChargeDialog(false);
    setOpen(false);
  };

  const [openUserFilter, setOpenUserFilter] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleCloseUserFilterDialog = () => {
    setOpenUserFilter(false);
    setOpen(false);
  };

  const getSelectedUser = (selectedUser) => {
    setSelectedUser(selectedUser);
  };

  // create new charge
  const onCreateAddCharge = (event) => {
    event.preventDefault();

    const chargeData = {
      user_id: selectedUser ? selectedUser.id : '',
      position_id: chargePosition ? chargePosition.id : '',
      start_date: chargeStartDate,
      end_date: chargeEndDate,
      email: chargeInstitutionalEmail,
      entity_id: federationID,
      country_id: federationCountry.id,
      province_id: federationProvince.id,
      city_id: federationCity.id,
    };

    let itemsLeft;

    Object.keys(chargeData).forEach((key) => {
      const value = chargeData[key];

      if (key !== 'end_date' && key !== 'email') {
        if (Object.prototype.hasOwnProperty.call(chargeData, key)) {
          if (value === undefined || value === null || value === 'Invalid Data' || value === '') {
            itemsLeft = true;

            handleOpenFederationNewPositionDataSnack();

            setTimeout(() => {
              handleCloseFederationNewPositionDataSnack();
            }, POPUP_DURATION_SUCCESS);
          }
        }
      }
    });

    if (!itemsLeft) {
      if (chargeData.end_date === '' || dayjs(chargeData.end_date).isAfter(dayjs(chargeData.start_date))) {
        API.post(`/charges`, chargeData)
          .then((response) => {
            setHasCreationSuccess(true);

            setPopUpText('Informazioni salvate');
            setOpen(true);

            setTimeout(() => {
              setOpen(false);
              setOpenAddChargeDialog(false);
              refreshChargeDialog();
              refreshCharges();
            }, POPUP_DURATION_SUCCESS);
          })
          .catch((error) => {
            setHasCreationSuccess(false);
            HttpErrorHandler(error, setPopUpText);
            setOpen(true);

            setTimeout(() => {
              setOpen(false);
            }, POPUP_DURATION_ERROR);
          });
      } else {
        setPopUpText('La data di fine non può essere antecedente a quella di inizio');
        setHasCreationSuccess(false);
        setOpen(true);
        setTimeout(() => {
          setOpen(false);
        }, POPUP_DURATION_ERROR);
      }
    }
  };

  // refresh new charge dialog
  const refreshChargeDialog = () => {
    setSelectedUser(null);
    setChargePosition('');
    setChargeInstitutionalEmail('');
    setChargeStartDate('');
    setChargeEndDate('');
  };

  // get organization data with expand of position by entity level
  const refreshCharges = () => {
    setErrorMessage(null);

    API.get(`/entities/${federationID}?expand=positions,parents`)
      .then((response) => {
        setFederationEntityType(response.data.entity_type);
        setFederationName(response.data.name);
        setFederationEmail(response.data.email);
        setFederationCallCode({
          id: response.data.country.id,
          name: response.data.country.name,
          call_code: response.data.call_code ? response.data.call_code : '',
        });
        setFederationPhone(response.data.phone);
        setFederationStreet(response.data.street);
        setFederationPostalCode(response.data.zip_code);
        setFederationCity(response.data.city);
        setFederationCityName(response.data.city_name);
        setFederationRegionName(response.data.region_name);
        setFederationProvince(response.data.province);
        setFederationRegion({ region_name: response.data.province?.region_name });
        setFederationCountry(response.data.country);
        setFederationIsMaster(response.data.master);
        setFederationParents(response.data.parent_entities);

        setFederationEntityData(response.data);
        generateChargeList(response.data.position);

        setCanModifyEntity(response.data.auth_type === 'W' || response.data.auth_type === 'D');
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => setHasFederation(true));
  };

  // get address data for countries, entity types
  const getAddressApi = () => {
    if (countryList.length === 0) {
      axios
        .all([API.get(`/countries`), API.get(`/etypes`)])
        .then(
          axios.spread((countryResponse, entityTypeResponse) => {
            setCountryList(countryResponse.data);
            // const entityTypeList = entityTypeResponse.data.entity_types.filter((type) => type.level !== 0);
            setEntityTypeList(entityTypeResponse.data.entity_types);
          })
        )
        .catch((error) => {
          setHasCreationSuccess(false);
          HttpErrorHandler(error, setPopUpText);
          setOpen(true);

          setTimeout(() => {
            setOpen(false);
          }, POPUP_DURATION_ERROR);
        });
    }
  };

  useEffect(() => {
    const italyData = countryList.find((element) => element.name === 'Italia');
    setItaly(italyData);
  }, [countryList]);

  useEffect(() => {
    if (isModify && federationCountry && federationCountry?.name !== italy?.name) {
      setFederationProvince('');
    }
  }, [federationCountry]);

  // Get RegionList
  useEffect(() => {
    if (isModify && federationCountry !== null && federationCountry !== undefined && federationCountry !== '') {
      if (typeof federationCountry === 'object' && Object.keys(federationCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: federationCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setRegionList(response.data);
          } else {
            setRegionList([]);
            setProvinceList([]);
            setCityList([]);
          }
        });
      }
    }
  }, [federationCountry, isModify]);

  // Get ProvincesList
  useEffect(() => {
    if (isModify && federationRegion !== null && federationRegion !== undefined && federationRegion !== '') {
      if (typeof federationRegion === 'object' && Object.keys(federationRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: federationRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setProvinceList(response.data);
          } else {
            setProvinceList([]);
            setCityList([]);
          }
        });
      }
    }
  }, [federationRegion, isModify]);

  // Get CitiesList
  useEffect(() => {
    if (isModify && federationProvince !== null && federationProvince !== undefined && federationProvince !== '') {
      if (typeof federationProvince === 'object' && Object.keys(federationProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: federationProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCityList(response.data);
          } else {
            setCityList([]);
          }
        });
      }
    }
  }, [federationProvince, isModify]);

  // Get api call for positions
  const getChargeApi = () => {
    if (chargePositionList.length === 0) {
      API.get(`/political_positions`)
        .then((response) => {
          setChargePositionList(response.data.political_positions);
        })
        .catch((error) => {
          setHasCreationSuccess(false);
          HttpErrorHandler(error, setPopUpText);
          setOpen(true);

          setTimeout(() => {
            setOpen(false);
          }, POPUP_DURATION_ERROR);
        });
    }
  };

  // update federation data
  const onUpdateFederation = (e) => {
    e.preventDefault();

    const federationData = {
      entity_type_id: federationEntityType ? federationEntityType.id : null,
      name: federationName.length > 0 ? federationName : null,
      email: federationEmail?.length > 0 ? federationEmail : null,
      call_code: federationCallCode ? federationCallCode.call_code : null,
      phone: federationPhone?.length > 0 ? federationPhone : null,
      street: federationStreet?.length > 0 ? federationStreet : null,
      zip_code: federationPostalCode,
      country_id: federationCountry ? federationCountry.id : null,
      master: federationIsMaster ? 1 : 0,
    };

    if (federationData.country_id === italy.id) {
      federationData.province_id = federationProvince ? federationProvince.id : null;
      federationData.region_id = federationRegion ? federationRegion.region_name : null;

      federationData.city_id = federationCity ? federationCity.id : null;
    } else {
      delete federationData.province_id;
      delete federationData.city_id;
      delete federationData.region_id;

      federationData.region_name = federationRegionName;
      federationData.city_name = federationCityName;
    }

    let itemsLeft;

    Object.keys(federationData).forEach((key) => {
      const value = federationData[key];

      if (key !== 'email' && key !== 'call_code' && key !== 'phone' && key !== 'street' && key !== 'zip_code') {
        if (Object.prototype.hasOwnProperty.call(federationData, key)) {
          if (value === undefined || value === null || value === '') {
            itemsLeft = true;
            handleOpenEditFederationDataSnack();

            setTimeout(() => {
              handleCloseEditFederationDataSnack();
            }, POPUP_DURATION_DELAY);
          }
        }
      }
    });

    if (!itemsLeft) {
      delete federationData.region_id;

      API.put(`/entities/${federationID}`, federationData)
        .then((response) => {
          setModify(false);
          setHasCreationSuccess(true);

          // success popup handler
          setPopUpText('Informazioni salvate');
          setOpen(true);
          setTimeout(() => {
            setOpen(false);
          }, POPUP_DURATION_SUCCESS);
        })
        .catch((error) => {
          setHasCreationSuccess(false);
          setPopUpText('Si è verificato un errore');
          setOpen(true);
          setTimeout(() => {
            setOpen(false);
          }, POPUP_DURATION_SUCCESS);
        });
    }
  };

  // generate position list
  const generateChargeList = (array) => {
    positions.length = 0;
    array.forEach((subArray) => {
      if (Array.isArray(subArray)) {
        subArray.forEach((data, index) => {
          const key = `${data.row_id}${index}`;
          data.key = key;
          positions.push(data);
        });
      } else {
        positions.push(subArray);
      }
    });
  };

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
    setOpen(false);
  };

  const getSelectedEntity = (selectedEntity) => {
    onAddParentEntity(selectedEntity.id);
  };

  // initial data loading
  useEffect(() => {
    refreshCharges();
  }, []);

  const [openDeleteRelation, setOpenDeleteRelation] = useState(false);
  const [parentToDelete, setParentToDelete] = useState({});

  const handleOpenDeleteRelation = (parent) => {
    setOpenDeleteRelation(true);
    setParentToDelete(parent);
  };

  const handleCloseDeleteRelation = () => {
    setOpenDeleteRelation(false);
    setParentToDelete({});
    setOpen(false);
  };

  const onDeleteRelation = (e) => {
    e.preventDefault();

    const data = {
      parent_id: parentToDelete.id,
      entity_id: federationID,
    };

    API.put(`/erels`, data)
      .then(() => {
        handleCloseDeleteRelation();
        refreshCharges();
      })
      .catch((error) => {
        setHasCreationSuccess(false);
        HttpErrorHandler(error, setPopUpText);
        handleCloseDeleteRelation();
        setOpen(true);

        setTimeout(() => {
          setOpen(false);
        }, POPUP_DURATION_ERROR);
      });
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  // if doesn't found federation id redirect to 404 page
  if (!federationID) return <Navigate to={{ pathname: '/404' }} />;

  return hasFederation ? (
    <Container maxWidth="xl" sx={{ position: 'relative' }}>
      {!errorMessage ? (
        <Box sx={{ width: '100%' }}>
          <Typography
            sx={{
              height: '24px',
              width: 'fit-content',
              whiteSpace: 'nowrap',
              borderRadius: '6px',
              padding: '0 10px',
              textAlign: 'center',
              fontWeight: '700',
              fontSize: '1.1em',
            }}
          >
            {federationEntityData.name}
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {/* tabs header */}
            <Tabs value={value} variant="fullWidth" onChange={handleChange} aria-label="Federation Details Page Tab">
              <Tab label="Anagrafica" {...a11yProps(0, 'thriple')} icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Cariche" {...a11yProps(1, 'thriple')} icon={<GppGoodIcon />} iconPosition="start" />
              {federationEntityData.entity_type.level !== 0 ? (
                <Tab
                  label="Livelli Superiori"
                  {...a11yProps(2, 'thriple')}
                  icon={<AccountTreeIcon />}
                  iconPosition="start"
                />
              ) : null}
            </Tabs>
          </Box>

          {/* federation data */}
          <ThripleTabPanel value={value} index={0}>
            <Card sx={{ minWidth: 275 }}>
              <CardContent>
                <form onSubmit={onUpdateFederation}>
                  {isModify &&
                  countryList.length === 0 &&
                  regionList.length === 0 &&
                  provinceList.length === 0 &&
                  cityList.length === 0 ? (
                    LoadingUI()
                  ) : (
                    <Grid container spacing={3}>
                      {/* Federation name */}
                      <Grid item xs={12}>
                        <CustomTextField
                          required
                          disabled={!isModify}
                          name="Federation-Name"
                          label="Nome"
                          defaultValue={federationName}
                          onChange={(e) => setFederationName(e.currentTarget.value)}
                        />
                      </Grid>

                      {/* Federation E-mail */}
                      <Grid item xs={12}>
                        <CustomTextField
                          disabled={!isModify}
                          name="Federation-Email"
                          label="E-mail"
                          type="email"
                          defaultValue={federationEmail}
                          onChange={(e) => setFederationEmail(e.currentTarget.value)}
                        />
                      </Grid>

                      {/* federation entity type */}
                      <Grid item xs={12}>
                        <CustomAutocomplete
                          id="federation-entity-type"
                          disabled={!isModify || federationEntityData.level === 0}
                          options={entityTypeList}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          defaultValue={{
                            id: federationEntityData.entity_type.id,
                            name: federationEntityData.entity_type.name,
                          }}
                          onChange={(e, value) => setFederationEntityType(value)}
                          renderInput={(params) => <TextField {...params} label="Tipo entità *" variant="outlined" />}
                        />
                      </Grid>

                      {/* Federation Call-Code */}
                      <Grid item xs={4}>
                        <CustomAutocomplete
                          disabled={!isModify}
                          id="Federation-call-code"
                          options={countryList}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.call_code}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name} {option.call_code}
                            </Box>
                          )}
                          filterOptions={(options, state) => {
                            const newOptions = [];

                            options.forEach((element) => {
                              if (
                                element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                                element.call_code.toLowerCase().includes(state.inputValue.toLowerCase())
                              ) {
                                newOptions.push(element);
                              }
                            });

                            return newOptions;
                          }}
                          defaultValue={{
                            id: federationEntityData.country.id,
                            name: federationEntityData.country.name,
                            call_code: federationEntityData.call_code ?? '',
                          }}
                          onChange={(e, value) => setFederationCallCode(value)}
                          renderInput={(params) => <TextField {...params} label="Prefisso" variant="outlined" />}
                        />
                      </Grid>

                      {/* Federation phone */}
                      <Grid item xs={8}>
                        <CustomTextField
                          disabled={!isModify}
                          type="tel"
                          name="Federation-Phone"
                          label="Contatto Telefonico"
                          defaultValue={federationPhone}
                          onChange={(event) => validatePhoneNumber(event, setFederationPhone)}
                        />
                      </Grid>

                      {/* Federation street */}
                      <Grid item xs={9}>
                        <CustomTextField
                          disabled={!isModify}
                          name="Federation-Street"
                          label="Indirizzo"
                          defaultValue={federationStreet}
                          onChange={(e) => setFederationStreet(e.currentTarget.value)}
                        />
                      </Grid>

                      {/* Federation Postal Code */}
                      <Grid item xs={3}>
                        <CustomTextField
                          disabled={!isModify}
                          name="Federation-Postal-code"
                          label="CAP"
                          defaultValue={federationPostalCode}
                          onChange={(e) => setFederationPostalCode(e.currentTarget.value)}
                        />
                      </Grid>

                      {/* federation country */}
                      <Grid item xs={12} md={6}>
                        <CustomAutocomplete
                          id="federation-country"
                          disabled={!isModify}
                          options={
                            federationCountry?.name === italy?.name
                              ? countryList.filter(
                                  (country) =>
                                    (!federationRegion || country.id === federationRegion.country_id) &&
                                    (!federationProvince || country.id === federationProvince.country_id)
                                )
                              : countryList
                          }
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          defaultValue={{
                            id: federationEntityData.country.id,
                            name: federationEntityData.country.name,
                          }}
                          onChange={(e, value) => setFederationCountry(value)}
                          renderInput={(params) => <TextField {...params} label="Paese *" variant="outlined" />}
                        />
                      </Grid>

                      {/* federation region */}
                      <Grid item xs={12} md={6}>
                        <CustomAutocomplete
                          required
                          freeSolo={federationCountry?.name !== italy?.name}
                          id="federation-region"
                          disabled={!isModify}
                          options={regionList.filter(
                            (region) =>
                              (!federationProvince || region.region_name === federationProvince.region_name) &&
                              (!federationCountry || region.country_id === federationCountry.id)
                          )}
                          isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.region_name;
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.region_name}>
                              {option.region_name}
                            </Box>
                          )}
                          defaultValue={
                            federationRegion?.region_name
                              ? {
                                  region_name: federationEntityData.province?.region_name,
                                  country_id: federationEntityData.country_id,
                                }
                              : federationRegionName
                          }
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setFederationRegion(value);
                            } else if (value && value.inputValue) {
                              setFederationRegion(value.inputValue);
                            } else {
                              setFederationRegion(value);
                            }
                          }}
                          onInputChange={(e, value) => {
                            setFederationRegionName(value);
                          }}
                          renderInput={(params) => <TextField {...params} label="Regione *" variant="outlined" />}
                        />
                      </Grid>

                      {/* federation province */}
                      <Grid item xs={12} md={6}>
                        <CustomAutocomplete
                          id="federation-province"
                          disabled={!isModify || (federationCountry && italy && italy.name !== federationCountry?.name)}
                          freeSolo={federationCountry?.name !== italy?.name}
                          options={provinceList.filter(
                            (province) =>
                              (!federationCountry || province.country_id === federationCountry.id) &&
                              (!federationRegion || province.region_name === federationRegion.region_name) &&
                              (!federationCity || province.id === federationCity.province_id)
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.name;
                          }}
                          value={{
                            id: federationProvince?.id,
                            name: federationProvince?.name ?? '',
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setFederationProvince(value);
                            } else if (value && value.inputValue) {
                              setFederationProvince(value.inputValue);
                            } else {
                              setFederationProvince(value);
                            }
                          }}
                          renderInput={(params) => <TextField {...params} label="Provincia *" variant="outlined" />}
                        />
                      </Grid>

                      {/* federation city */}
                      <Grid item xs={12} md={6}>
                        <CustomAutocomplete
                          id="federation-city"
                          disabled={!isModify}
                          freeSolo={federationCountry?.name !== italy?.name}
                          required
                          options={cityList.filter((city) =>
                            federationProvince ? city.province_id === federationProvince.id : true
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.name;
                          }}
                          defaultValue={{
                            id: federationCity?.id,
                            name: federationCity?.name ?? federationCityName,
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setFederationCity(value);
                            } else if (value && value.inputValue) {
                              setFederationCity(value.inputValue);
                            } else {
                              setFederationCity(value);
                            }
                          }}
                          onInputChange={(e, value) => {
                            setFederationCityName(value);
                          }}
                          renderInput={(params) => <TextField {...params} label="Comune *" variant="outlined" />}
                        />
                      </Grid>

                      {/* entity master */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              disabled={!isModify}
                              checked={!!federationIsMaster}
                              onChange={() => setFederationIsMaster(!federationIsMaster)}
                            />
                          }
                          label="Eleggibile per il tesseramento"
                        />
                      </Grid>

                      {/* federation form submit buttons */}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          sx={{ '& button': { m: '0 10px' }, display: canModifyEntity ? 'flex' : 'none' }}
                        >
                          <DefaultOutlinedButton
                            id="Federation-cancel-button"
                            variant="outlined"
                            customStyles={{ display: isModify ? 'block' : 'none' }}
                            onClick={() => setModify(false)}
                          >
                            Annulla
                          </DefaultOutlinedButton>
                          <DefaultContainedButton
                            id="Federation-modify-button"
                            variant="contained"
                            customStyles={{ display: isModify ? 'none' : 'block' }}
                            onClick={() => {
                              getAddressApi();
                              setModify(true);
                            }}
                          >
                            Modifica
                          </DefaultContainedButton>
                          <DefaultContainedButton
                            id="Federation-save-button"
                            variant="contained"
                            type="submit"
                            customStyles={{ display: isModify ? 'block' : 'none' }}
                          >
                            Salva
                          </DefaultContainedButton>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </form>
              </CardContent>
            </Card>
          </ThripleTabPanel>

          {/* federation charges */}
          <ThripleTabPanel value={value} index={1}>
            <Box>
              {/* Add new charge dialog button */}
              <Box display={canModifyEntity ? 'flex' : 'none'} justifyContent="flex-end">
                <DefaultContainedButton onClick={onOpenAddChargeDialog} startIcon={<AddIcon />}>
                  Aggiungi Carica
                </DefaultContainedButton>
              </Box>

              {/* new charge dialog */}
              <Dialog open={openAddChargeDialog} fullWidth onClose={() => {}}>
                <form onSubmit={onCreateAddCharge}>
                  {/* new charge title */}
                  <DialogTitle textAlign="center">Nuova Carica</DialogTitle>

                  {/* new charge dialog content */}
                  <DialogContent>
                    {chargePositionList.length !== 0 ? (
                      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ marginTop: 1 }}>
                        <Grid item xs={12}>
                          {!selectedUser ? (
                            <DefaultOutlinedButton
                              customStyles={{ width: '100%' }}
                              onClick={() => setOpenUserFilter(!openUserFilter)}
                            >
                              Cerca Utente
                            </DefaultOutlinedButton>
                          ) : (
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <CustomTextField
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                        <Tooltip title="Modifica" placement="left" arrow>
                                          <EditIcon onClick={() => setOpenUserFilter(!openUserFilter)} />
                                        </Tooltip>
                                      </InputAdornment>
                                    ),
                                  }}
                                  value={selectedUser.name}
                                  label="Utente *"
                                />
                              </Grid>
                            </Grid>
                          )}

                          <UserFilterDialog
                            openDialog={openUserFilter}
                            closeDialog={handleCloseUserFilterDialog}
                            outputUser={getSelectedUser}
                          />
                        </Grid>

                        {/* charge position */}
                        <Grid item xs={12}>
                          <CustomAutocomplete
                            id="charge-position"
                            options={chargePositionList}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            onChange={(e, value) => setChargePosition(value)}
                            renderInput={(params) => <TextField {...params} label="Tipo carica *" variant="outlined" />}
                          />
                        </Grid>

                        {/* charge institutional e-mail */}
                        <Grid item xs={12}>
                          <CustomTextField
                            fullWidth
                            margin="dense"
                            id="charge-email"
                            value={chargeInstitutionalEmail}
                            onChange={(e) => setChargeInstitutionalEmail(e.currentTarget.value)}
                            label="E-mail"
                            type="email"
                          />
                        </Grid>

                        {/* charge start date */}
                        <Grid item xs={12} sm={6}>
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                            <CustomDatePicker
                              label="Data Inizio *"
                              onChange={handleStartDateChange}
                              maxDate={dayjs(chargeEndDate)}
                            />
                          </LocalizationProvider>
                        </Grid>

                        {/* charge end date */}
                        <Grid item xs={12} sm={6}>
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                            <CustomDatePicker
                              label="Data Fine"
                              onChange={handleEndDateChange}
                              minDate={dayjs(chargeStartDate)}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </Grid>
                    ) : (
                      LoadingUI()
                    )}
                  </DialogContent>

                  {/* new charge dialog footer buttons */}
                  <DialogActions>
                    <DefaultOutlinedButton onClick={onCloseAddChargeDialog}>Chiudi</DefaultOutlinedButton>
                    {chargePositionList.length !== 0 && (
                      <DefaultContainedButton type="submit">Aggiungi</DefaultContainedButton>
                    )}
                    <CustomSnackbar
                      open={openFederationNewPositionDataSnack}
                      handleClose={handleCloseFederationNewPositionDataSnack}
                      message="Inserisci i dati mancanti!"
                    />
                  </DialogActions>
                </form>
                {isDialogOpen === 'newPosition' && displayPopup()}
              </Dialog>
            </Box>

            <Card sx={{ minWidth: 275, marginTop: '24px' }}>
              {/* position table */}
              {positions.length !== 0 ? (
                <TableContainer sx={{ padding: '24px 24px 6px' }}>
                  <Table size="small" aria-label="Roles table">
                    {/* position table header */}
                    <TableHead>
                      <TableRow>
                        <TableCell>Stato</TableCell>
                        <TableCell>Nome Utente</TableCell>
                        <TableCell>Tipo Carica</TableCell>
                        {is600Screen && <TableCell>Inizio carica</TableCell>}
                        {is600Screen && <TableCell>Fine carica</TableCell>}
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    {/* position table body */}
                    <TableBody>
                      {positions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((position) => (
                        <TableRow hover key={position.id}>
                          <TableCell>{PositionStatusLabel(position, 'icon')}</TableCell>
                          <TableCell component="th" scope="row">
                            {position.user.name} {position.user.surname}
                          </TableCell>
                          <TableCell>{position.position.name}</TableCell>
                          {is600Screen && (
                            <>
                              <TableCell>
                                {position.start_date ? dayjs(position.start_date).format('DD/MM/YYYY') : '//'}
                              </TableCell>
                              <TableCell>
                                {position.end_date ? dayjs(position.end_date).format('DD/MM/YYYY') : '//'}
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <CustomIconButton
                              size="small"
                              onClick={() =>
                                navigate('/admin/organization/charge-details', {
                                  state: { chargeID: position.id, canModify: canModifyEntity },
                                })
                              }
                            >
                              <KeyboardArrowRight />
                            </CustomIconButton>
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* empty table row */}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 47 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>

                    {/* position table footer */}
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          sx={{ borderBottom: 'none' }}
                          rowsPerPageOptions={[]}
                          colSpan={6}
                          count={positions.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          SelectProps={{
                            inputProps: {
                              'aria-label': 'rows per page',
                            },
                            native: true,
                          }}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={TablePaginationActions}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              ) : (
                EmptyPageUI('Nessuna carica trovata')
              )}
            </Card>
          </ThripleTabPanel>

          {/* livello superiori */}
          {federationEntityData.level !== 0 ? (
            <ThripleTabPanel value={value} index={2}>
              <Box>
                {/* Add new parents entity dialog button */}
                <Box display="flex" justifyContent="flex-end">
                  <DefaultContainedButton
                    endIcon={<LaunchIcon />}
                    onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)}
                    customStyles={{display: canModifyEntity ? 'flex' : 'none'}}
                  >
                    Includi In...
                  </DefaultContainedButton>

                  <EntitiesDialog
                    openDialog={openEntitiesFilter}
                    closeDialog={handleCloseEntitiesFilterDialog}
                    outputEntity={getSelectedEntity}
                    context={'orgStructure'}
                  />
                </Box>
              </Box>

              <Card sx={{ minWidth: 275, marginTop: '24px' }}>
                <DecisionDialog
                  openDialog={openDeleteRelation}
                  dialogTitle={'Elimina Relazione'}
                  dialogDescription={`Sei sicuro di voler eliminare la relazione con questa entità?`}
                  closeOperation={handleCloseDeleteRelation}
                  requestOperation={onDeleteRelation}
                  requestText={'Elimina'}
                  closeText={'Chiudi'}
                  RequestButton={RedOutlinedButton}
                />
                {/* parent entities table */}
                {federationParents.length !== 0 ? (
                  <TableContainer sx={{ padding: '24px 24px 6px' }}>
                    <Table size="small" aria-label="Roles table">
                      {/* parent entity table header */}
                      <TableHead>
                        <TableRow>
                          <TableCell>Tipo Entità</TableCell>
                          <TableCell>Nome Entità</TableCell>
                          <TableCell>Regione</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>

                      {/* parent entity table body */}
                      <TableBody>
                        {(rowsPerPage > 0
                          ? federationParents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : federationParents
                        ).map((parent) => (
                          <TableRow hover key={parent.id}>
                            <TableCell>{entityTypeLabel(parent, true)}</TableCell>
                            <TableCell>{parent.name}</TableCell>
                            <TableCell>{parent.province?.region_name ?? parent.region_name}</TableCell>
                            <TableCell>
                              <Tooltip title="Elimina Relazione" arrow placement="top">
                                <>
                                  <CustomIconButton 
                                    sx={{display: canModifyEntity ? 'flex' : 'none'}}
                                    onClick={() => handleOpenDeleteRelation(parent)}>
                                    <DeleteIcon />
                                  </CustomIconButton>
                                </>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}

                        {/*  empty table row  */}
                        {emptyRows > 0 && (
                          <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={4} />
                          </TableRow>
                        )}
                      </TableBody>

                      {/* position table footer */}
                      {federationParents.length > rowsPerPage && (
                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              sx={{ borderBottom: 'none' }}
                              rowsPerPageOptions={[]}
                              colSpan={3}
                              count={positions.length}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              SelectProps={{
                                inputProps: {
                                  'aria-label': 'rows per page',
                                },
                                native: true,
                              }}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              ActionsComponent={TablePaginationActions}
                            />
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </TableContainer>
                ) : (
                  EmptyPageUI('Nessun livello Superiore')
                )}
              </Card>
            </ThripleTabPanel>
          ) : null}

          {isDialogOpen === 'noDialog' && displayPopup()}

          {/* snackbar UI */}
          <CustomSnackbar
            open={openEditFederationDataSnack}
            handleClose={handleCloseEditFederationDataSnack}
            message="Inserisci i dati mancanti!"
          />
        </Box>
      ) : (
        ErrorUI(errorMessage)
      )}
    </Container>
  ) : (
    LoadingUI()
  );
}